import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
    GraduationCap,
    Target,
    BookOpen,
    CheckCircle,
    XCircle,
    Clock,
    TrendingUp,
    Play,
    Eye,
    Loader2,
    RefreshCw,
    Filter,
    ArrowUpDown,
    CheckCircle2,
    Award,
    Layers,
    FileText,
    Brain,
    Users
} from 'lucide-react';

const JobSimulationsAnalytics = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [skillProgress, setSkillProgress] = useState([]);
    const [skills, setSkills] = useState({});
    const [stats, setStats] = useState(null);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [sortField, setSortField] = useState('started_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterStatus, setFilterStatus] = useState('all');
    const [verticals, setVerticals] = useState([]);
    const [filterVertical, setFilterVertical] = useState('all');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch user progress
            const progressRes = await api.get('/job-simulations/progress');
            const progressData = progressRes.data?.skills || [];
            setSkillProgress(progressData);
            
            // Fetch verticals for filtering
            try {
                const verticalsRes = await api.get('/job-simulations/verticals');
                setVerticals(verticalsRes.data || []);
            } catch (e) {
                setVerticals([]);
            }
            
            // Fetch skill details for each progress entry
            const skillDetails = {};
            for (const progress of progressData) {
                try {
                    const skillRes = await api.get(`/job-simulations/skills/${progress.skill_id}`);
                    skillDetails[progress.skill_id] = skillRes.data;
                } catch (e) {
                    skillDetails[progress.skill_id] = { name: progress.skill_id, category: {} };
                }
            }
            setSkills(skillDetails);
            
            // Calculate stats
            const total = progressData.length;
            const completed = progressData.filter(p => p.overall_status === 'completed').length;
            const inProgress = progressData.filter(p => p.overall_status === 'in_progress').length;
            const totalLessons = progressData.reduce((sum, p) => sum + (p.lessons_completed || 0), 0);
            const totalSimulations = progressData.reduce((sum, p) => sum + (p.simulations_completed || 0), 0);
            
            // Calculate avg completion rate
            const completionRates = progressData
                .filter(p => p.total_lessons > 0)
                .map(p => (p.lessons_completed / p.total_lessons) * 100);
            const avgCompletionRate = completionRates.length > 0
                ? Math.round(completionRates.reduce((a, b) => a + b, 0) / completionRates.length)
                : 0;
            
            setStats({
                total,
                completed,
                inProgress,
                notStarted: total - completed - inProgress,
                totalLessons,
                totalSimulations,
                avgCompletionRate
            });
            
        } catch (error) {
            console.error('Failed to fetch job simulations data:', error);
            toast.error('Failed to load job simulations data');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'completed':
                return <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">Completed</Badge>;
            case 'in_progress':
                return <Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-300">In Progress</Badge>;
            default:
                return <Badge variant="outline" className="text-muted-foreground">Not Started</Badge>;
        }
    };

    const getProgressColor = (progress) => {
        if (progress >= 100) return 'bg-emerald-500';
        if (progress >= 50) return 'bg-blue-500';
        if (progress > 0) return 'bg-amber-500';
        return 'bg-gray-300';
    };

    const viewSkillDetail = (progress) => {
        setSelectedSkill({
            ...progress,
            skill: skills[progress.skill_id] || {}
        });
        setShowDetailModal(true);
    };

    const continueSkill = (skillId) => {
        navigate(`/skill-learning/${skillId}`);
    };

    // Sort and filter
    const sortedProgress = [...skillProgress]
        .filter(p => filterStatus === 'all' || p.overall_status === filterStatus)
        .filter(p => {
            if (filterVertical === 'all') return true;
            const skill = skills[p.skill_id];
            return skill?.category?.vertical_id === filterVertical;
        })
        .sort((a, b) => {
            let aVal, bVal;
            switch(sortField) {
                case 'lessons':
                    aVal = a.lessons_completed || 0;
                    bVal = b.lessons_completed || 0;
                    break;
                case 'status':
                    const statusOrder = { completed: 3, in_progress: 2, not_started: 1 };
                    aVal = statusOrder[a.overall_status] || 0;
                    bVal = statusOrder[b.overall_status] || 0;
                    break;
                case 'skill_name':
                    aVal = skills[a.skill_id]?.name || '';
                    bVal = skills[b.skill_id]?.name || '';
                    break;
                default:
                    aVal = new Date(a.started_at || 0);
                    bVal = new Date(b.started_at || 0);
            }
            if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
            return aVal < bVal ? 1 : -1;
        });

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="job-sims-analytics">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <GraduationCap className="h-7 w-7 text-primary" />
                            Job Simulations Analytics
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Track your skill learning progress across all job categories
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={fetchData}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                        <Button onClick={() => navigate('/job-simulations')}>
                            <Play className="h-4 w-4 mr-2" />
                            Browse Skills
                        </Button>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.total || 0}</p>
                            <p className="text-xs text-muted-foreground">Skills Started</p>
                        </CardContent>
                    </Card>
                    <Card className="border-emerald-500/30 bg-emerald-500/5">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{stats?.completed || 0}</p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Completed</p>
                        </CardContent>
                    </Card>
                    <Card className="border-blue-500/30 bg-blue-500/5">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats?.inProgress || 0}</p>
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">In Progress</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.notStarted || 0}</p>
                            <p className="text-xs text-muted-foreground">Not Started</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.totalLessons || 0}</p>
                            <p className="text-xs text-muted-foreground">Lessons Done</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.totalSimulations || 0}</p>
                            <p className="text-xs text-muted-foreground">Simulations</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{stats?.avgCompletionRate || 0}%</p>
                            <p className="text-xs text-muted-foreground">Avg Progress</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Progress Overview by Status */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-primary" />
                            Learning Progress Overview
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                                <div className="flex items-center gap-3">
                                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                                    <div>
                                        <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats?.completed || 0}</p>
                                        <p className="text-sm text-emerald-600 dark:text-emerald-400">Skills Mastered</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                                <div className="flex items-center gap-3">
                                    <BookOpen className="h-8 w-8 text-blue-500" />
                                    <div>
                                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats?.inProgress || 0}</p>
                                        <p className="text-sm text-blue-600 dark:text-blue-400">In Progress</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/50 border">
                                <div className="flex items-center gap-3">
                                    <Target className="h-8 w-8 text-muted-foreground" />
                                    <div>
                                        <p className="text-2xl font-bold">{stats?.notStarted || 0}</p>
                                        <p className="text-sm text-muted-foreground">Yet to Start</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Filter:</span>
                    </div>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="not_started">Not Started</SelectItem>
                        </SelectContent>
                    </Select>
                    {verticals.length > 0 && (
                        <Select value={filterVertical} onValueChange={setFilterVertical}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Vertical" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Verticals</SelectItem>
                                {verticals.map(v => (
                                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <span className="text-sm text-muted-foreground ml-auto">
                        Showing {sortedProgress.length} of {skillProgress.length} skills
                    </span>
                </div>

                {/* Skills Table */}
                <Card>
                    <CardContent className="p-0">
                        <ScrollArea className="h-[500px]">
                            <Table>
                                <TableHeader className="sticky top-0 bg-background">
                                    <TableRow>
                                        <TableHead className="w-[250px]">
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('skill_name')} className="h-8 px-2">
                                                Skill
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>Category</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('status')} className="h-8 px-2">
                                                Status
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('lessons')} className="h-8 px-2">
                                                Progress
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>Simulations</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('started_at')} className="h-8 px-2">
                                                Started
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedProgress.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                {skillProgress.length === 0 
                                                    ? "No skills started yet. Browse skills to begin learning!"
                                                    : "No skills match your filters"
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sortedProgress.map((progress) => {
                                            const skill = skills[progress.skill_id] || {};
                                            const lessonsProgress = progress.total_lessons > 0 
                                                ? Math.round((progress.lessons_completed / progress.total_lessons) * 100) 
                                                : 0;
                                            
                                            return (
                                                <TableRow 
                                                    key={progress.id} 
                                                    className="cursor-pointer hover:bg-muted/50"
                                                    onClick={() => viewSkillDetail(progress)}
                                                >
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className="p-1.5 rounded bg-primary/10">
                                                                <GraduationCap className="h-4 w-4 text-primary" />
                                                            </div>
                                                            <span className="font-medium">{skill.name || progress.skill_id}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">
                                                            {skill.category?.name || '-'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>{getStatusBadge(progress.overall_status)}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2 min-w-[120px]">
                                                            <Progress 
                                                                value={lessonsProgress} 
                                                                className={`h-2 flex-1 [&>div]:${getProgressColor(lessonsProgress)}`}
                                                            />
                                                            <span className="text-xs text-muted-foreground w-16">
                                                                {progress.lessons_completed}/{progress.total_lessons}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm">{progress.simulations_completed || 0}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">
                                                            {progress.started_at 
                                                                ? formatDistanceToNow(new Date(progress.started_at), { addSuffix: true })
                                                                : '-'
                                                            }
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                continueSkill(progress.skill_id);
                                                            }}
                                                        >
                                                            <Play className="h-4 w-4" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Detail Modal */}
                <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <div className="p-2 rounded bg-primary/10">
                                    <GraduationCap className="h-5 w-5 text-primary" />
                                </div>
                                {selectedSkill?.skill?.name || 'Skill Details'}
                            </DialogTitle>
                            <DialogDescription>
                                {selectedSkill?.skill?.category?.name || 'Category'} • {selectedSkill?.skill?.difficulty_base || 'Beginner'}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {selectedSkill && (
                            <div className="space-y-4">
                                {/* Progress Stats */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">
                                                {selectedSkill.lessons_completed}/{selectedSkill.total_lessons}
                                            </p>
                                            <p className="text-xs text-muted-foreground">Lessons</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSkill.simulations_completed || 0}</p>
                                            <p className="text-xs text-muted-foreground">Simulations</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            {selectedSkill.job_analysis_completed ? (
                                                <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto" />
                                            ) : (
                                                <XCircle className="h-6 w-6 text-muted-foreground mx-auto" />
                                            )}
                                            <p className="text-xs text-muted-foreground mt-1">Job Analysis</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            {selectedSkill.candidate_screening_completed ? (
                                                <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto" />
                                            ) : (
                                                <XCircle className="h-6 w-6 text-muted-foreground mx-auto" />
                                            )}
                                            <p className="text-xs text-muted-foreground mt-1">Screening</p>
                                        </CardContent>
                                    </Card>
                                </div>
                                
                                {/* Status */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Status</span>
                                        {getStatusBadge(selectedSkill.overall_status)}
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Difficulty</span>
                                        <Badge variant="outline" className="capitalize">{selectedSkill.difficulty_level || 'beginner'}</Badge>
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Started</span>
                                        <span>{selectedSkill.started_at ? format(new Date(selectedSkill.started_at), 'PP') : 'Not started'}</span>
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Completed</span>
                                        <span>{selectedSkill.completed_at ? format(new Date(selectedSkill.completed_at), 'PP') : '-'}</span>
                                    </div>
                                </div>
                                
                                {/* Lesson Progress */}
                                {selectedSkill.lesson_progress?.length > 0 && (
                                    <div className="p-3 rounded-lg bg-muted/50">
                                        <h4 className="font-medium mb-2 flex items-center gap-2">
                                            <BookOpen className="h-4 w-4 text-primary" />
                                            Lesson Progress
                                        </h4>
                                        <div className="space-y-2">
                                            {selectedSkill.lesson_progress.map((lp, idx) => (
                                                <div key={lp.lesson_id} className="flex items-center justify-between text-sm p-2 bg-background rounded">
                                                    <span>Lesson {idx + 1}</span>
                                                    <div className="flex items-center gap-2">
                                                        {lp.quiz_passed ? (
                                                            <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                                                                Passed ({lp.quiz_score}%)
                                                            </Badge>
                                                        ) : lp.quiz_attempts > 0 ? (
                                                            <Badge variant="outline">
                                                                Attempt {lp.quiz_attempts}
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="text-muted-foreground">
                                                                Not attempted
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Description */}
                                {selectedSkill.skill?.description && (
                                    <div className="p-3 rounded-lg bg-muted/50">
                                        <h4 className="font-medium mb-2">About this Skill</h4>
                                        <p className="text-sm text-muted-foreground">
                                            {selectedSkill.skill.description}
                                        </p>
                                    </div>
                                )}
                                
                                {/* Actions */}
                                <div className="flex gap-2 pt-2">
                                    <Button 
                                        variant="outline" 
                                        className="flex-1"
                                        onClick={() => setShowDetailModal(false)}
                                    >
                                        Close
                                    </Button>
                                    <Button 
                                        className="flex-1"
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            continueSkill(selectedSkill.skill_id);
                                        }}
                                    >
                                        <Play className="h-4 w-4 mr-2" />
                                        {selectedSkill.overall_status === 'completed' ? 'Review' : 'Continue'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default JobSimulationsAnalytics;
